import { ref } from 'vue';

// 모달 스택 관리 (전역 변수)
const modalStack = ref<string[]>([]);

// 모달 UID와 닫기 콜백을 매핑하는 Map
const modalCloseCallbacks = new Map<string, () => void>();

// 전역 ESC 키 핸들러 (한 번만 등록)
let escHandlerRegistered = false;

function handleGlobalEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    // eslint-disable-next-line no-console
    console.log('[ModalStore] Global ESC pressed. modalStack:', modalStack.value);
    // 최상위 모달의 닫기 콜백 실행
    if (modalStack.value.length > 0) {
      const topModalUID = modalStack.value[modalStack.value.length - 1];
      const closeCallback = modalCloseCallbacks.get(topModalUID);
      if (closeCallback) {
        closeCallback();
      }
    }
  }
}

// 전역 ESC 핸들러 등록
function registerGlobalEscHandler() {
  if (!escHandlerRegistered && typeof window !== 'undefined') {
    document.addEventListener('keydown', handleGlobalEscKey);
    escHandlerRegistered = true;
  }
}

// 전역 ESC 핸들러 해제 (모든 모달이 닫혔을 때만)
function unregisterGlobalEscHandler() {
  if (escHandlerRegistered && modalStack.value.length === 0 && typeof window !== 'undefined') {
    document.removeEventListener('keydown', handleGlobalEscKey);
    escHandlerRegistered = false;
  }
}

// 메인 스토어 함수
export const useModalStore = () => {
  // 모달 스택에 추가
  const pushModalStack = (modalUID: string, closeCallback: () => void) => {
    if (!modalStack.value.includes(modalUID)) {
      modalStack.value.push(modalUID);
      modalCloseCallbacks.set(modalUID, closeCallback);
      // 첫 번째 모달이 등록될 때 전역 ESC 핸들러 등록
      if (modalStack.value.length === 1) {
        registerGlobalEscHandler();
      }
    }
  };

  // 모달 스택에서 제거
  const popModalStack = (modalUID: string) => {
    const idx = modalStack.value.indexOf(modalUID);
    if (idx !== -1) {
      modalStack.value.splice(idx, 1);
      modalCloseCallbacks.delete(modalUID);
      // 모든 모달이 닫혔을 때 전역 ESC 핸들러 해제
      if (modalStack.value.length === 0) {
        unregisterGlobalEscHandler();
      }
    }
  };

  // 최상위 모달인지 확인
  const isTopModal = (modalUID: string) => {
    return modalStack.value[modalStack.value.length - 1] === modalUID;
  };

  // 모달을 최상위로 이동
  const bringToFront = (modalUID: string) => {
    if (modalStack.value[modalStack.value.length - 1] !== modalUID) {
      // 스택에서 내가 있던 위치를 지우고 맨 뒤로(최상위) 보냄
      const idx = modalStack.value.indexOf(modalUID);
      if (idx !== -1) {
        modalStack.value.splice(idx, 1);
        modalStack.value.push(modalUID);
        // 콜백도 함께 이동 (순서는 이미 맞춰져 있음)
      }
    }
  };

  return {
    // 일반 변수
    modalStack,
    // 함수
    pushModalStack,
    popModalStack,
    isTopModal,
    bringToFront,
  };
};

